import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { useAdmin } from '../context/AdminContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import AdminServicePhotos from './AdminServicePhotos';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminGalleryManagement = () => {
  const { token } = useAdmin();
  const { language } = useLanguage();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPhoto, setNewPhoto] = useState({
    title: '',
    description: '',
    service_type: '',
    before_image: '',
    after_image: ''
  });

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/gallery`);
      setPhotos(response.data);
    } catch (error) {
      console.error('Error fetching photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPhoto = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/gallery`, newPhoto, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(language === 'pt' ? 'Foto adicionada com sucesso!' : 'Photo added successfully!');
      setNewPhoto({ title: '', description: '', service_type: '', before_image: '', after_image: '' });
      setShowAddForm(false);
      fetchPhotos();
    } catch (error) {
      console.error('Error adding photo:', error);
      alert(language === 'pt' ? 'Erro ao adicionar foto' : 'Error adding photo');
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm(language === 'pt' ? 'Tem certeza que deseja excluir?' : 'Are you sure you want to delete?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/gallery/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPhotos();
    } catch (error) {
      console.error('Error deleting photo:', error);
    }
  };

  return (
    <div className="space-y-6">
      <AdminServicePhotos token={token} language={language} onPublish={(p) => { setNewPhoto({ ...newPhoto, ...p }); setShowAddForm(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-heading font-semibold">
          {language === 'pt' ? 'Galeria do Site (Antes/Depois)' : 'Website Gallery (Before/After)'}
        </h2>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-light"
          data-testid="add-photo-button"
        >
          <Plus size={20} className="mr-2" />
          {language === 'pt' ? 'Adicionar Foto' : 'Add Photo'}
        </button>
      </div>

      {showAddForm && (
        <form onSubmit={handleAddPhoto} className="bg-gray-50 p-6 rounded-lg space-y-4" data-testid="add-photo-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder={language === 'pt' ? 'Título' : 'Title'}
              value={newPhoto.title}
              onChange={(e) => setNewPhoto({...newPhoto, title: e.target.value})}
              required
              className="px-4 py-2 border rounded-md"
            />
            <input
              type="text"
              placeholder={language === 'pt' ? 'Tipo de Serviço' : 'Service Type'}
              value={newPhoto.service_type}
              onChange={(e) => setNewPhoto({...newPhoto, service_type: e.target.value})}
              required
              className="px-4 py-2 border rounded-md"
            />
          </div>
          <textarea
            placeholder={language === 'pt' ? 'Descrição' : 'Description'}
            value={newPhoto.description}
            onChange={(e) => setNewPhoto({...newPhoto, description: e.target.value})}
            required
            className="w-full px-4 py-2 border rounded-md"
            rows="3"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="url"
              placeholder={language === 'pt' ? 'URL Imagem Antes' : 'Before Image URL'}
              value={newPhoto.before_image}
              onChange={(e) => setNewPhoto({...newPhoto, before_image: e.target.value})}
              required
              className="px-4 py-2 border rounded-md"
            />
            <input
              type="url"
              placeholder={language === 'pt' ? 'URL Imagem Depois' : 'After Image URL'}
              value={newPhoto.after_image}
              onChange={(e) => setNewPhoto({...newPhoto, after_image: e.target.value})}
              required
              className="px-4 py-2 border rounded-md"
            />
          </div>
          <div className="flex space-x-2">
            <button type="submit" className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-light">
              {language === 'pt' ? 'Salvar' : 'Save'}
            </button>
            <button type="button" onClick={() => setShowAddForm(false)} className="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400">
              {language === 'pt' ? 'Cancelar' : 'Cancel'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p>{language === 'pt' ? 'Carregando...' : 'Loading...'}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {photos.map((photo, idx) => (
            <div key={photo.id} className="bg-white border rounded-lg p-4" data-testid={`admin-photo-${idx}`}>
              <div className="flex space-x-2 mb-2">
                <img src={photo.before_image} alt="Before" className="w-1/2 h-32 object-cover rounded" />
                <img src={photo.after_image} alt="After" className="w-1/2 h-32 object-cover rounded" />
              </div>
              <h3 className="font-semibold">{photo.title}</h3>
              <p className="text-sm text-gray-600">{photo.service_type}</p>
              <button
                onClick={() => handleDeletePhoto(photo.id)}
                className="mt-2 flex items-center text-red-600 hover:text-red-800"
                data-testid={`delete-photo-${idx}`}
              >
                <Trash2 size={16} className="mr-1" />
                {language === 'pt' ? 'Excluir' : 'Delete'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminGalleryManagement;
